import { useMutation, useQuery } from "@tanstack/react-query";
import { useFormik } from "formik";
import React, { useRef, useCallback, useState } from "react";
import { Card, Form, Modal, Button } from "react-bootstrap";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AsyncPaginate } from "react-select-async-paginate";
import * as yup from "yup";
import Swal from "sweetalert2";
import NavigateBack from "../../common/NavigateBack";
import {
  addUser,
  getUserDetails,
  getUsers,
  updateUser,
  uploadDocument,
  deleteDocument,
  parseDateInput1,
} from "../../services/services";
import { constant } from "../../utils/constants";
import { toastAlert } from "../../utils/SweetAlert";
import { noSpecialChars } from "../../utils/function";
import * as images from "../../assets/image";
import DatePicker from "react-datepicker";
import moment from "moment";

// Function to parse MMDDYYYY input and convert to Date object
const parseDateInput = (input) => {
  const cleanedInput = input.replace(/\D/g, ""); // Remove non-digits
  if (cleanedInput.length === 8) {
    const month = parseInt(cleanedInput.slice(0, 2), 10);
    const day = parseInt(cleanedInput.slice(2, 4), 10);
    const year = parseInt(cleanedInput.slice(4, 8), 10);

    // Validate month, day, and year
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 1900) {
      const date = new Date(year, month - 1, day);
      // Check if the date is valid
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        return date;
      }
    }
  }
  return null; // Return null for invalid input
};

const AddClient = () => {
  const IMAGE_BASE_URL =
    import.meta.env.VITE_DOCUMENT_API_URL ||
    import.meta.env.VITE_API_URL.replace("api/v1", "");
  const [searchParam] = useSearchParams();
  const id = searchParam?.get("id");
  const dateRef = useRef();
  const navigate = useNavigate();
  const authDropdownRef = useRef(null);
  const noAutoDropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setValues,
    touched,
    handleSubmit,
  } = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      clientHourlyRate: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      note: "",
      dob: null,
      role: constant.ROLE.CLIENT,
      authorizedEmployee: [],
      authorizedNoAutoAssign: [],
      documents: [],
    },
    validationSchema: yup.object().shape({
      firstName: yup
        .string()
        .required()
        .label("First name")
        .min(2)
        .trim()
        .matches(
          /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
          "First name should contain letters only"
        ),
      lastName: yup
        .string()
        .required()
        .label("Last name")
        .min(2)
        .trim()
        .matches(
          /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
          "Last name should contain letters only"
        ),
      email: yup.string().required().email().label("Email").trim(),
      phoneNumber: yup
        .string()
        .min(5)
        .label("Phone Number")
        .test("phone-validate", "Invalid phone number", function (value) {
          if (value?.length > 6) {
            return isValidPhoneNumber(String(value));
          } else {
            return true;
          }
        }),
      clientHourlyRate: yup
        .number()
        .required()
        .label("Rate")
        .positive("Rate must be a positive number")
        .test(
          "is-decimal",
          "Rate must have up to two decimal places",
          (value) => {
            if (value === undefined || value === null) return false;
            return /^\d+(\.\d{1,2})?$/.test(value.toString());
          }
        ),
      address: yup.string().required().label("Address").trim().min(2),
      city: yup.string().required().label("City").trim().min(2),
      state: yup.string().required().label("State").trim().min(2),
      zipCode: yup
        .string()
        .required()
        .label("Zip Code")
        .trim()
        .min(2)
        .matches(/^[a-zA-Z0-9]*$/, "Invalid zip code"),
      dob: yup
        .date()
        .required("Date of Birth is required")
        .max(new Date(), "Date of Birth cannot be in the future")
        .label("Date of Birth"),
      authorizedEmployee: yup
        .array()
        .min(1, "At least one authorized employee is required")
        .required()
        .label("Authorized Employees"),
      authorizedNoAutoAssign: yup.array().optional(),
      documents: yup.array().optional(),
    }),
    onSubmit: (values) => {
      let dob_DATE = moment(values.dob);
      let body = {
        firstName: values?.firstName?.trim(),
        lastName: values?.lastName?.trim(),
        email: values?.email,
        phoneNumber: values?.phoneNumber,
        clientHourlyRate: parseFloat(values?.clientHourlyRate),
        address: values?.address,
        city: values?.city,
        state: values?.state,
        zipCode: values?.zipCode,
        note: values?.note?.trim() || "",
        dob: dob_DATE.format(constant.DATE_ONLY_FORMAT),
        authorizedEmployees: values?.authorizedEmployee?.map((i) => i.value),
        authorizedNoAutoAssign: values?.authorizedNoAutoAssign?.map(
          (i) => i.value
        ),
        role: constant.ROLE.CLIENT,
        documents: values?.documents.map(({ _id, ...restdoc }) => ({
          ...restdoc,
        })),
      };
      mutation.mutate(body);
    },
  });

  const mutation = useMutation({
    mutationFn: async (body) => (id ? updateUser(id, body) : addUser(body)),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      navigate("../client");
    },
   
  });

  const { data: documentsData, refetch: refetchDocuments } = useQuery({
    queryKey: ["user-documents", id],
    queryFn: async () => {
      if (id) {
        const resp = await getUserDetails(id);
        return resp?.data?.data?.documents || [];
      }
      return [];
    },
    enabled: !!id,
    onSuccess: (data) => {
      setFieldValue("documents", data);
    },
  });

  useQuery({
    queryKey: ["user-details", id],
    queryFn: async () => {
      const resp = !!id && (await getUserDetails(id));
      let data = resp?.data?.data;
      if (data) {
        setValues({
          ...values,
          firstName: data?.firstName,
          lastName: data?.lastName,
          email: data?.email,
          phoneNumber: data?.phoneNumber,
          clientHourlyRate: data?.clientHourlyRate?.toFixed(2) || "",
          address: data?.userAddress?.address?.trim(),
          city: data?.userAddress?.city?.trim(),
          state: data?.userAddress?.state?.trim(),
          zipCode: data?.userAddress?.zipCode,
          note: data?.note?.trim(),
         dob: data.dob ? moment(data.dob).toDate() : null,
          role: constant.ROLE.CLIENT,
          authorizedEmployee:
            data?.authorizedDetails?.map((i) => ({
              label: `${i.firstName} ${i.lastName}`,
              value: i._id,
            })) || [],
          authorizedNoAutoAssign:
            data?.authorizedNoAutoAssignDetails?.map((i) => ({
              label: `${i.firstName} ${i.lastName}`,
              value: i._id,
            })) || [],
          documents: data?.documents || [],
        });
      }
      return true;
    },
    enabled: !!id,
  });

  const documentMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("userId", id);
      const response = await uploadDocument(formData);
      return response;
    },
    onSuccess: (resp) => {
      toastAlert("success", "Document uploaded successfully");
      const newDocument = {
        filePath: resp?.data?.data?.documents?.filePath,
        fileName: resp?.data?.data?.documents?.fileName,
      };
      setFieldValue("documents", [...values.documents, newDocument]);
      fileInputRef.current.value = null;
    },
  
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId) => {
      return await deleteDocument(documentId);
    },
    onSuccess: (resp, documentId) => {
      toastAlert("success", "Document deleted successfully");
      setFieldValue(
        "documents",
        values.documents.filter((doc) => doc._id !== documentId)
      );
    },
   
  });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toastAlert("error", "File size exceeds 5MB limit");
        return;
      }
      if (
        ![
          "application/pdf",
          "image/jpeg",
          "image/png",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(file.type)
      ) {
        toastAlert("error", "Only PDF, JPEG, and PNG files are allowed");
        return;
      }
      documentMutation.mutate(file);
    }
  };

  const handlePreview = (doc) => {
    if (doc.filePath && getFileType(doc.filePath) === "application/pdf") {
      const filePath = `${IMAGE_BASE_URL}${doc.filePath}`;
      window.open(filePath, "_blank");
      return;
    } else {
      setPreviewDoc(doc);
      setShowPreview(true);
    }
  };

  const handleDelete = (doc, index) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This document will be deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        if (!doc._id) {
          setFieldValue(
            "documents",
            values.documents.filter((d, i) => i !== index)
          );
          toastAlert("success", "Document deleted successfully");
        } else {
          deleteDocumentMutation.mutate(doc._id);
        }
      }
    });
  };

  const getFileName = (doc) => {
    return doc?.fileName || "Unknown Document";
  };

  const getFileType = (filePath) => {
    if (!filePath || typeof filePath !== "string") {
      return "unknown";
    }

    const extension = filePath.split(".").pop()?.toLowerCase();

    if (extension === "pdf") {
      return "application/pdf";
    } else if (extension === "docx") {
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    } else {
      return `image/${extension}`;
    }
  };

  const employeeList = useCallback(
    async (search, loadedOptions, { page }) => {
      try {
        let params = {
          page: page,
          limit: 100,
          role: constant.ROLE.EMPLOYEE,
        };
        if (search) params.search = search;
        let resp = await getUsers(params);
        let array = (await resp?.data?.data?.users) ?? [];

        const filteredArray = array.filter(
          (employee) =>
            !values.authorizedNoAutoAssign.some(
              (noAutoEmp) => noAutoEmp.value === employee._id
            )
        );

        return {
          options: filteredArray.map((i) => ({
            label: `${i.firstName} ${i.lastName}`,
            value: i._id,
          })),
          hasMore: resp?.data?.data?.total_pages > page,
          additional: {
            page: page + 1,
          },
        };
      } catch (error) {
        return {
          options: [],
          hasMore: false,
          additional: { page: page + 1 },
        };
      }
    },
    [values.authorizedNoAutoAssign]
  );

  const nonAutoAssignEmployeeList = useCallback(
    async (search, loadedOptions, { page }) => {
      try {
        let params = {
          page: page,
          limit: 100,
          role: constant.ROLE.EMPLOYEE,
        };
        if (search) params.search = search;
        let resp = await getUsers(params);
        let array = (await resp?.data?.data?.users) ?? [];

        const filteredArray = array.filter(
          (employee) =>
            !values.authorizedEmployee.some(
              (authEmp) => authEmp.value === employee._id
            )
        );

        return {
          options: filteredArray.map((i) => ({
            label: `${i.firstName} ${i.lastName}`,
            value: i._id,
          })),
          hasMore: resp?.data?.data?.total_pages > page,
          additional: {
            page: page + 1,
          },
        };
      } catch (error) {
        return {
          options: [],
          hasMore: false,
          additional: { page: page + 1 },
        };
      }
    },
    [values.authorizedEmployee]
  );

  const handleRateChange = (e) => {
    let value = e.target.value.replace(/[^0-9.]/g, "");
    const parts = value.split(".");
    if (parts[1]?.length > 2) {
      value = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
    setFieldValue("clientHourlyRate", value);
  };

  const handleSelectChange = (
    field,
    selectedOptions,
    otherField,
    dropdownRef
  ) => {
    const newSelection = selectedOptions || [];
    setFieldValue(field, newSelection);
    setFieldValue(
      otherField,
      values[otherField].filter(
        (item) =>
          !newSelection.some((selected) => selected.value === item.value)
      )
    );
    if (dropdownRef.current) {
      dropdownRef.current.focus();
    }
  };

  return (
    <>
      <section className="main-content">
        <div className="addclientPage">
          <NavigateBack>{!!id ? "Edit Client" : "Add Client"}</NavigateBack>
          <Card border="light" bg="light" className="p-4">
            <Form onSubmit={handleSubmit}>
              <div className="clientInputBox">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        First Name <span className="text-danger">*</span>
                      </label>
                      <input
                        className="inputBox"
                        type="text"
                        placeholder="Enter First Name"
                        name="firstName"
                        value={values?.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.firstName && errors?.firstName}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Last Name <span className="text-danger">*</span>
                      </label>
                      <input
                        className="inputBox"
                        type="text"
                        name="lastName"
                        value={values?.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter Last Name"
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.lastName && errors?.lastName}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group position-relative">
                      <label className="labelTxt">
                        DOB <span className="text-danger">*</span>
                      </label>
                      <img
                        src={images.calendar}
                        className="calanderIcon"
                        alt="calendarImg"
                        role="button"
                        onClick={() => dateRef?.current?.setFocus()}
                      />
                      <DatePicker
                        onChange={(date) => setFieldValue("dob", date)}
                        selected={values?.dob}
                        maxDate={new Date()}
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        dateFormat="MM/dd/yyyy"
                        placeholderText="MM/DD/YYYY"
                        className="inputBox"
                        ref={dateRef}
                        onBlur={() => setFieldTouched("dob", true)}
                        onChangeRaw={(e) => {
                          if (e.target && e.target.value) {
                            // Check if e.target and e.target.value exist
                            const input = e.target.value.trim();
                            if (/^\d{8}$/.test(input)) {
                              // Check for exactly 8 digits (MMDDYYYY)
                              const parsedDate = parseDateInput(input); // Use parseDateInput (fix typo if parseDateInput1 was intended)
                              if (parsedDate) {
                                setFieldValue("dob", parsedDate);
                              }
                            }
                          }
                        }}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.dob && errors?.dob}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        className="inputBox"
                        type="text"
                        id="email"
                        placeholder="Enter Email"
                        name="email"
                        value={values?.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.email && errors?.email}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Contact No <span className="text-danger">*</span>
                      </label>
                      <PhoneInput
                        defaultCountry={constant.DEFAULT_COUNTRY}
                        value={values?.phoneNumber}
                        onChange={(phone) =>
                          setFieldValue("phoneNumber", phone)
                        }
                        onBlur={() => setFieldTouched("phoneNumber", true)}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.phoneNumber && errors?.phoneNumber}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Rate <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          className="inputBox form-control"
                          type="text"
                          placeholder="0.00"
                          name="clientHourlyRate"
                          value={values?.clientHourlyRate}
                          onChange={handleRateChange}
                          onBlur={handleBlur}
                        />
                      </div>
                      <small className="text-danger requiredTxt">
                        {touched?.clientHourlyRate && errors?.clientHourlyRate}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Address <span className="text-danger">*</span>
                      </label>
                      <input
                        className="inputBox"
                        type="text"
                        placeholder="Enter Address"
                        name="address"
                        value={values?.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.address && errors?.address}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        City <span className="text-danger">*</span>
                      </label>
                      <input
                        className="inputBox"
                        type="text"
                        placeholder="Enter City"
                        name="city"
                        onKeyDown={noSpecialChars}
                        value={values?.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.city && errors?.city}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        State <span className="text-danger">*</span>
                      </label>
                      <input
                        className="inputBox"
                        type="text"
                        placeholder="Enter State"
                        name="state"
                        onKeyDown={noSpecialChars}
                        value={values?.state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.state && errors?.state}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Zipcode <span className="text-danger">*</span>
                      </label>
                      <input
                        className="inputBox"
                        type="text"
                        style={{ textTransform: "uppercase" }}
                        placeholder="Enter Zipcode"
                        name="zipCode"
                        value={values?.zipCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        maxLength={6}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.zipCode && errors?.zipCode}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Authorized Employees{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <AsyncPaginate
                        ref={authDropdownRef}
                        isMulti
                        additional={{ page: 1 }}
                        styles={constant.REACT_SELECT_CUSTOM_STYLE}
                        debounceTimeout={500}
                        loadOptions={employeeList}
                        value={values?.authorizedEmployee || []}
                        onChange={(selectedOptions) => {
                          handleSelectChange(
                            "authorizedEmployee",
                            selectedOptions,
                            "authorizedNoAutoAssign",
                            authDropdownRef
                          );
                        }}
                        placeholder="Select authorized employees..."
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        menuShouldScrollIntoView={true}
                        cacheOptions={true}
                        cacheUniqs={[
                          JSON.stringify(
                            values.authorizedNoAutoAssign.map(
                              (item) => item.value
                            )
                          ),
                        ]}
                      />
                      <small className="text-danger requiredTxt">
                        {touched?.authorizedEmployee &&
                          errors?.authorizedEmployee}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">
                        Authorized Employees (non-automatic)
                      </label>
                      <AsyncPaginate
                        ref={noAutoDropdownRef}
                        isMulti
                        additional={{ page: 1 }}
                        styles={constant.REACT_SELECT_CUSTOM_STYLE}
                        debounceTimeout={500}
                        loadOptions={nonAutoAssignEmployeeList}
                        value={values?.authorizedNoAutoAssign || []}
                        onChange={(selectedOptions) => {
                          handleSelectChange(
                            "authorizedNoAutoAssign",
                            selectedOptions,
                            "authorizedEmployee",
                            noAutoDropdownRef
                          );
                        }}
                        placeholder="Select non-automatic employees..."
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        menuShouldScrollIntoView={true}
                        cacheOptions={true}
                        cacheUniqs={[
                          JSON.stringify(
                            values.authorizedEmployee.map((item) => item.value)
                          ),
                        ]}
                      />
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">Note</label>
                      <textarea
                        rows={1}
                        name="note"
                        className="inputBox"
                        value={values?.note}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <small className="text-danger requiredTxt">
                        {touched.note && errors.note}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-12 mb-3">
                    <div className="form-group">
                      <label className="labelTxt">Documents</label>
                      <div className="input-group mb-3">
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleFileUpload}
                          accept=".pdf,.jpg,.jpeg,.png"
                        />
                        <Button
                          onClick={() => fileInputRef.current.click()}
                          disabled={documentMutation.isLoading}
                          className="btn-blue-outline d-flex align-items-center"
                        >
                          {documentMutation.isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Uploading...
                            </>
                          ) : (
                            "Attach"
                          )}
                        </Button>
                      </div>
                      {values.documents?.length > 0 && (
                        <div className="document-list">
                          {values.documents.map((doc, index) => (
                            <div
                              key={doc._id || doc.filePath}
                              className="document-item"
                            >
                              <span
                                className="document-name text-truncate d-inline-block me-2"
                                onClick={() => handlePreview(doc)}
                                title={getFileName(doc)}
                              >
                                {getFileName(doc)}
                              </span>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(doc, index)}
                                disabled={
                                  deleteDocumentMutation.isLoading &&
                                  deleteDocumentMutation.variables === doc._id
                                }
                              >
                                {deleteDocumentMutation.isLoading &&
                                deleteDocumentMutation.variables === doc._id
                                  ? "Deleting..."
                                  : "Delete"}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="staffBtn mt-4">
                <button
                  className="addBtn me-3"
                  type="submit"
                  disabled={mutation.isLoading}
                >
                  {!!id ? "Update" : "Add"}
                </button>
                <Link className="cancleBtn" role="button" to={-1}>
                  Cancel
                </Link>
              </div>
            </Form>
          </Card>
        </div>
      </section>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Document Preview: {previewDoc && getFileName(previewDoc)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewDoc && (
            <div className="d-flex justify-center">
              {getFileType(previewDoc.filePath) === "application/pdf" ? (
                <a
                  href={`${IMAGE_BASE_URL}${previewDoc.filePath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View PDF
                </a>
              ) : getFileType(previewDoc.filePath) ===
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ? (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                    `${IMAGE_BASE_URL}${previewDoc.filePath}`
                  )}`}
                  width="100%"
                  height="500px"
                  frameBorder="0"
                  title="DOCX Preview"
                />
              ) : ["image/jpeg", "image/png"].includes(
                  getFileType(previewDoc.filePath)
                ) ? (
                <img
                  src={`${IMAGE_BASE_URL}${previewDoc.filePath}`}
                  alt="Document Preview"
                  className="img-fluid mx-auto"
                  style={{ maxWidth: "100%", maxHeight: "500px" }}
                />
              ) : (
                <p>Unsupported file type for preview</p>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddClient;
